import { isValidCasNumber } from './cas-number.validator';

/**
 * A CAS-shaped token bounded by non-digit/non-hyphen characters so we don't
 * match a fragment of a longer number. The leading boundary is a capture group
 * (rather than a look-behind) to stay compatible with older Safari/WebKit.
 */
const CAS_TOKEN = /(^|[^\d-])(\d{2,7}-\d{2}-\d)(?![\d-])/g;

/** How many characters before a match to inspect for a "CAS" label. */
const CONTEXT_WINDOW = 12;
const CAS_LABEL = /cas/i;

/**
 * Heading of the SDS composition section (section 3) — "AVSNITT 3:
 * Sammensetning/opplysninger om bestanddeler", "SECTION 3:
 * Composition/information on ingredients" and the Danish/Swedish/German
 * equivalents. The section label ("AVSNITT", "SECTION", …) is optional because
 * some sheets number the heading bare ("3. Sammensetning …"), but the title
 * keyword is required so plain cross-references ("se avsnitt 3") don't match.
 * The leading boundary is a capture group instead of a look-behind (Safari) and
 * keeps "13." from matching as section 3.
 */
const COMPOSITION_HEADING =
  /(^|[^0-9])(?:avsnitt|seksjon|section|punkt|del|kapittel|abschnitt)?[\s:.]*0*3\s*[.:)–-]?\s*(?:sammensetning|sammensætning|sammansättning|sammansattning|composition|zusammensetzung)/gi;

/**
 * Where the composition section ends: the next numbered section heading
 * (labelled form, sections 4–16) or a bare "4" followed by the first-aid title
 * used by section 4.
 */
const NEXT_SECTION_HEADING =
  /(^|[^0-9])(?:(?:avsnitt|seksjon|section|punkt|del|kapittel|abschnitt)[\s:.]*0*(?:1[0-6]|[4-9])\b|0*4\s*[.:)–-]?\s*(?:førstehjelp|første\s*hjelp|forstehjelp|first[\s-]?aid|förste?\s*hjälpen|erste[\s-]?hilfe))/gi;

/**
 * Fallback length for the composition section when no following section heading
 * can be found. Section 3 is a short ingredient table; this keeps us from
 * swallowing the toxicology tables further down the document.
 */
const MAX_SECTION_LENGTH = 4000;

interface TextMatch {
  /** Index of the matched heading itself (past the leading boundary character). */
  start: number;
  /** Index just after the matched heading. */
  end: number;
}

/** Every match of a global regex, with the leading boundary group stripped. */
function findHeadings(pattern: RegExp, text: string): TextMatch[] {
  const matches: TextMatch[] = [];
  pattern.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const start = match.index + match[1].length;
    matches.push({ start, end: match.index + match[0].length });
    if (match[0].length === match[1].length) {
      pattern.lastIndex++; // guard against a zero-width match stalling the loop
    }
  }
  return matches;
}

/**
 * De-duplicated, ranked valid CAS numbers in already-normalised text. Numbers
 * appearing right after a "CAS" label come first, then the remaining valid
 * numbers in document order.
 */
function collectCasNumbers(normalized: string): string[] {
  // value -> whether it ever appeared next to a "CAS" label. Map preserves
  // first-seen (document) order for the non-labelled tail.
  const seen = new Map<string, boolean>();

  CAS_TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = CAS_TOKEN.exec(normalized)) !== null) {
    const lead = match[1];
    const value = match[2];
    if (!isValidCasNumber(value)) {
      continue;
    }

    const valueIndex = match.index + lead.length;
    const context = normalized.slice(Math.max(0, valueIndex - CONTEXT_WINDOW), valueIndex);
    const labelled = CAS_LABEL.test(context);

    if (seen.has(value)) {
      if (labelled) {
        seen.set(value, true);
      }
    } else {
      seen.set(value, labelled);
    }
  }

  const entries = Array.from(seen.entries());
  const labelledFirst = entries.filter(([, labelled]) => labelled).map(([value]) => value);
  const rest = entries.filter(([, labelled]) => !labelled).map(([value]) => value);
  return [...labelledFirst, ...rest];
}

/**
 * The text of every plausible composition section, in document order. A sheet
 * can produce more than one candidate: the table of contents on page 1, a
 * repeated page header, and the real section. Each candidate runs from its
 * heading to the next numbered section heading (or {@link MAX_SECTION_LENGTH}
 * characters, whichever comes first).
 */
function compositionSectionCandidates(normalized: string): string[] {
  const nextSections = findHeadings(NEXT_SECTION_HEADING, normalized);

  return findHeadings(COMPOSITION_HEADING, normalized).map(heading => {
    const next = nextSections.find(section => section.start >= heading.end);
    const limit = heading.end + MAX_SECTION_LENGTH;
    const end = next ? Math.min(next.start, limit) : Math.min(limit, normalized.length);
    return normalized.slice(heading.start, end);
  });
}

/**
 * Scan free text (e.g. extracted from an SDS PDF) for valid CAS Registry
 * Numbers. Each candidate is checksum-validated with {@link isValidCasNumber}.
 *
 * Detection is scoped to the SDS composition section (section 3,
 * "Sammensetning/opplysninger om bestanddeler" / "Composition/information on
 * ingredients"), which is where the ingredient CAS numbers actually live.
 * Without that scoping a data sheet's base-oil and toxicology tables contribute
 * dozens of CAS-shaped numbers the user then has to prune by hand.
 *
 * We fall back to the whole document when no composition section yields a CAS
 * number — that covers both a sheet whose heading we can't recognise and a
 * table-of-contents/page-header false positive, which are indistinguishable
 * from each other here. The fallback means this never returns less than the
 * previous whole-document scan did.
 *
 * The result is de-duplicated and ranked so that numbers appearing right after
 * a "CAS" label come first, then remaining valid numbers in document order.
 * Returns an empty array when the text is empty (e.g. a scanned/image-only PDF).
 */
export function extractValidCasNumbers(text: string): string[] {
  if (!text) {
    return [];
  }

  // Normalise Unicode dashes and no-break spaces so the plain ASCII-hyphen
  // pattern matches numbers copied from styled PDFs.
  const normalized = text
    .replace(/[‐-―−]/g, '-') // hyphen/figure/en/em dashes, minus sign
    .replace(/[  ]/g, ' ');       // non-breaking / narrow no-break space

  for (const section of compositionSectionCandidates(normalized)) {
    const scoped = collectCasNumbers(section);
    if (scoped.length > 0) {
      return scoped;
    }
  }

  return collectCasNumbers(normalized);
}
